import React from 'react';
import {
  displayAnalyticsValue,
  displayUtmValue,
  formatTimestamp,
  shortId,
} from '../utils/analyticsDisplay';

const VisitorDossierHeader = ({
  filters,
  matchingCount,
  totalCount,
  recentEvents = [],
  onChange,
  onReset,
}) => {
  return (
    <section className="rounded-[32px] border border-[#e6d9c7] bg-white/90 p-6 shadow-[0_30px_90px_-58px_rgba(42,30,12,0.42)] sm:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8a6b42]">Drilldown</p>
          <h3 className="mt-2 text-2xl font-semibold text-[#1f1a14]">Filters and recent intent</h3>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="rounded-full border border-[#ebdecd] bg-[#f7f0e4] px-4 py-2 text-sm font-medium text-[#71583a]">
            {Number(matchingCount || 0).toLocaleString()} / {Number(totalCount || 0).toLocaleString()} visitors
          </span>
          <span className="rounded-full border border-[#ebdecd] bg-[#fbf7ef] px-4 py-2 text-sm font-medium text-[#71583a]">
            {Number(recentEvents.length || 0).toLocaleString()} recent events
          </span>
          <button
            type="button"
            onClick={onReset}
            className="rounded-full bg-[#2d5f79] px-4 py-2 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:bg-[#254e63]"
          >
            Reset filters
          </button>
        </div>
      </div>

      <div className="mt-6 grid gap-5 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.85fr)]">
        <div className="rounded-[28px] border border-[#e8dccd] bg-white/92 p-5">
          <div className="grid gap-4 md:grid-cols-2">
            <FilterField
              label="Search"
              value={filters.search}
              placeholder="User, path, referrer, utm source"
              onChange={(value) => onChange('search', value)}
              className="md:col-span-2"
            />
            <FilterField
              label="Referrer host"
              value={filters.referrerHost}
              placeholder="google.com, linkedin.com"
              onChange={(value) => onChange('referrerHost', value)}
            />
            <FilterField
              label="UTM source"
              value={filters.utmSource}
              placeholder="linkedin, newsletter"
              onChange={(value) => onChange('utmSource', value)}
            />
            <FilterField
              label="UTM medium"
              value={filters.utmMedium}
              placeholder="social, email, organic"
              onChange={(value) => onChange('utmMedium', value)}
            />
            <FilterField
              label="Landing path"
              value={filters.landingPath}
              placeholder="/, /projects, /resume"
              onChange={(value) => onChange('landingPath', value)}
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <ToggleChip
              active={Boolean(filters.returningOnly)}
              onClick={() => onChange('returningOnly', !filters.returningOnly)}
              label="Returning only"
            />
            <ToggleChip
              active={false}
              onClick={onReset}
              label="Clear"
            />
          </div>
        </div>

        <div className="rounded-[28px] border border-[#e8dccd] bg-white/92 p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#8b7a68]">Recent intent</p>
              <h4 className="mt-2 text-lg font-semibold text-[#1f1a14]">Latest human signals</h4>
            </div>
            <span className="rounded-full bg-[#f7efe3] px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#6a5132]">
              Operator
            </span>
          </div>

          <div className="mt-4 space-y-2">
            {recentEvents.length === 0 ? (
              <div className="rounded-[20px] border border-dashed border-[#e1d4c2] bg-[#fcf9f5] px-4 py-6">
                <p className="text-sm font-medium text-[#3f352c]">No recent intent yet.</p>
                <p className="mt-1 text-xs text-[#7d7266]">This panel fills as custom events arrive.</p>
              </div>
            ) : (
              recentEvents.slice(0, 8).map((event) => (
                <IntentRow key={`${event.eventType}-${event.sessionId}-${event.ts}`} event={event} />
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const FilterField = ({ label, value, onChange, placeholder, className = '' }) => (
  <label className={`block ${className}`.trim()}>
    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-[#8b7a68]">
      {label}
    </span>
    <input
      type="text"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="w-full rounded-[18px] border border-[#e5d8c7] bg-[#fcfaf7] px-4 py-3 text-sm text-[#231d17] outline-none transition placeholder:text-[#aa9f92] focus:border-[#b89972] focus:bg-white"
    />
  </label>
);

const ToggleChip = ({ active, onClick, label }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-full border px-4 py-2 text-sm font-semibold transition hover:-translate-y-0.5 ${
      active
        ? 'border-[#2d5f79] bg-[#2d5f79] text-white'
        : 'border-[#dacbb8] bg-[#faf5ee] text-[#69523a]'
    }`}
  >
    {label}
  </button>
);

const IntentRow = ({ event }) => {
  const title = humanizeEventType(event.eventType);
  const source = formatIntentSource(event);
  const path = displayAnalyticsValue(event.path, '/');

  return (
    <div className="rounded-[20px] border border-[#ece0d0] bg-[#fbf8f4] px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#4b3c2e]">
              {title}
            </span>
            {event.userId && (
              <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#6d5a43]">
                {shortId(event.userId)}
              </span>
            )}
          </div>
          <p className="mt-2 truncate text-sm font-medium text-[#2b241d]" title={path}>
            {path}
          </p>
          <p className="mt-1 truncate text-xs text-[#7d7266]" title={source}>
            {source}
          </p>
        </div>
        <span className="shrink-0 text-xs text-[#84796d]">
          {formatTimestamp(event.ts)}
        </span>
      </div>
    </div>
  );
};

const humanizeEventType = (eventType) => {
  switch (eventType) {
    case 'resume_open':
      return 'Resume open';
    case 'social_click':
      return 'Social click';
    case 'contact_click':
      return 'Contact click';
    case 'paper_pdf_open':
      return 'PDF open';
    case 'paper_code_click':
      return 'Code click';
    case 'external_link_click':
      return 'Outbound link';
    default: {
      const normalized = String(eventType || '').replace(/_/g, ' ').trim();
      if (!normalized) return 'Event';
      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    }
  }
};

const formatIntentSource = (event) => {
  const attribution = event?.attribution && typeof event.attribution === 'object' ? event.attribution : {};
  const referrerHost = displayAnalyticsValue(attribution.referrerHost, 'direct');
  const utmSource = displayUtmValue(attribution.utmSource, '');
  const utmMedium = displayUtmValue(attribution.utmMedium, '');
  const parts = [referrerHost];
  if (utmSource) parts.push(utmSource);
  if (utmMedium) parts.push(utmMedium);
  return parts.filter(Boolean).join(' · ') || 'direct';
};

export default VisitorDossierHeader;
